// Follow-up Stopwatch Modal (WP-04).
import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { saveFollowup, type FollowupPreset } from '../../lib/followups';
import type { LiveConversation } from '../../lib/inboxTypes';
import { ClockIcon, CloseIcon } from '../icons';

interface FollowupModalProps {
  conv: LiveConversation;
  onClose: () => void;
  onSuccess?: () => void;
}

const PRESETS: { id: FollowupPreset; label: string }[] = [
  { id: '1h', label: '+1h' },
  { id: '2h', label: '+2h' },
  { id: '4h', label: '+4h' },
  { id: '6h', label: '+6h' },
  { id: '12h', label: '+12h' },
  { id: '1d', label: '+1 day' },
  { id: '2d', label: '+2 days' },
  { id: '1w', label: '+1 week' },
];

export default function FollowupModal({ conv, onClose, onSuccess }: FollowupModalProps) {
  const { t } = useI18n();
  const [selectedPreset, setSelectedPreset] = useState<FollowupPreset | null>('1h');
  const [customDateTime, setCustomDateTime] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t('followup.reasonRequired') || 'Reason is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let dueAt: number | undefined = undefined;
      let preset: FollowupPreset | undefined = undefined;

      if (customDateTime) {
        dueAt = new Date(customDateTime).getTime();
        if (isNaN(dueAt) || dueAt <= Date.now()) {
          setError(t('followup.futureTimeRequired') || 'Please select a future time');
          setSaving(false);
          return;
        }
      } else if (selectedPreset) {
        preset = selectedPreset;
      }

      await saveFollowup({
        conversationId: conv.id,
        preset,
        dueAt,
        reason: reason.trim(),
        note: note.trim() || undefined,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to save follow-up');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
              <ClockIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('followup.modalTitle') || 'Set Follow-up Reminder'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {conv.displayName || conv.name} ({conv.phone})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
              {error}
            </div>
          )}

          {/* Presets */}
          <div>
            <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
              {t('followup.quickPresets') || 'Quick Presets'}
            </label>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              {PRESETS.map((p) => {
                const isSelected = selectedPreset === p.id && !customDateTime;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSelectedPreset(p.id);
                      setCustomDateTime('');
                    }}
                    className={cn(
                      'rounded-lg py-1.5 text-xs font-bold transition-colors border',
                      isSelected
                        ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                        : 'border-line bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-750',
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Date & Time */}
          <div>
            <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
              {t('followup.orSpecificTime') || 'Or Specific Date & Time'}
            </label>
            <input
              type="datetime-local"
              value={customDateTime}
              onChange={(e) => {
                setCustomDateTime(e.target.value);
                setSelectedPreset(null);
              }}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
              {t('followup.reason') || 'Follow-up Reason'} <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('followup.reasonPlaceholder') || 'e.g., Check if payment cleared, send catalog…'}
              className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
              {t('followup.note') || 'Internal Note (optional)'}
            </label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('followup.notePlaceholder') || 'Additional instructions for yourself or teammates…'}
              className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={saving || !reason.trim()}
              className="rounded-xl bg-amber-500 px-5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-amber-600 disabled:opacity-50"
            >
              {saving ? t('common.saving') || 'Saving…' : t('followup.saveReminder') || 'Set Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
